import type {MaterialWithRecipes} from '@domain/materials'
import type {OperationMode} from '@hconnect/common/types'
import {GenericInput} from '@hm/fundamental'
import {Box} from '@mui/material'
import {compact} from 'lodash'
import React, {useEffect, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'

import {InfoGroup, InfoGroupProps} from '../../../common/components'
import type {ScheduleItem} from '../../../common/types'

interface MaterialDeleteInfoProps {
  scheduleItems: ScheduleItem[]
  material: MaterialWithRecipes
  operationModes: OperationMode[]
  onConfirmationChange?: (isConfirmed: boolean) => void
}

function cutStringTo40Chars(str: string) {
  return str.length <= 40 ? str : `${str.substring(0, 37)}...`
}

// Prevents copying the material name and pasting it into the confirmation input
function blockClipboardEvent(event: React.ClipboardEvent<HTMLInputElement>) {
  event.preventDefault()
}

export const MaterialDeleteInfo: React.FC<MaterialDeleteInfoProps> = ({
  material,
  scheduleItems,
  operationModes,
  onConfirmationChange
}) => {
  const {t} = useTranslation()
  const [confirmationValue, setConfirmationValue] = useState('')

  const recipesStr = cutStringTo40Chars(material.recipes.map((r) => r.name).join(', '))
  const operationModesStr = cutStringTo40Chars(operationModes.map((mode) => mode.name).join(', '))

  const panels: InfoGroupProps['panels'] = compact([
    material.recipes.length > 0 && {
      accentColor: 'warning',
      content: (
        <Trans
          i18nKey="materialsSettings.deleteMaterialAssignedRecipes"
          values={{recipe_count: material.recipes.length, recipes: recipesStr}}
        />
      )
    },
    operationModes.length > 0 && {
      accentColor: 'warning',
      content: (
        <Trans
          i18nKey="materialsSettings.deleteMaterialAssignedOperationModes"
          values={{
            operation_mode_count: operationModes.length,
            operation_modes: operationModesStr
          }}
        />
      )
    },
    scheduleItems.length > 0 && {
      accentColor: 'warning',
      content: (
        <Trans
          i18nKey="assetsSettings.deleteOperationModesAssignedScheduleItems"
          values={{
            number: scheduleItems.length
          }}
        />
      )
    }
  ])

  const requiresNameConfirmation = panels.length > 0
  const isNameConfirmed =
    !requiresNameConfirmation || confirmationValue.trim() === material.name.trim()

  useEffect(() => {
    onConfirmationChange?.(isNameConfirmed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNameConfirmed])

  return (
    <>
      <InfoGroup
        description={
          <>
            <Trans i18nKey="materialsSettings.deleteMaterialIrreversibleWarning" />{' '}
            <Trans
              i18nKey="materialsSettings.deleteMaterialConfirmation"
              values={{name: material.name}}
            />
          </>
        }
        panels={panels}
      />
      {requiresNameConfirmation && (
        <Box sx={{mt: 2}}>
          <GenericInput
            data-cy="material_delete_confirm_input"
            placeholder={t('materialsSettings.deleteMaterialConfirmInputPlaceholder')}
            value={confirmationValue}
            onChange={setConfirmationValue}
            onCopy={blockClipboardEvent}
            onCut={blockClipboardEvent}
            onPaste={blockClipboardEvent}
            autoComplete="off"
          />
        </Box>
      )}
    </>
  )
}
